import { Controller } from 'stimulus'
import { DirectUpload } from '@rails/activestorage'
import Cropper from 'cropperjs'

// Artwork / profile pic uploader: drop, paste or pick an image, crop it
// square, then upload it straight to storage. The resulting signed blob id
// goes into a hidden field, so saving the form attaches it.
const ACCEPTED_TYPES = ['image/jpeg', 'image/png']
const MAX_BYTES = 10 * 1024 * 1024
const MIN_SIZE = 500
const MAX_OUTPUT = 2000

export default class extends Controller {
  static targets = ['preview', 'input', 'field', 'label', 'progress', 'progressBar', 'error', 'modal', 'cropImage',
                    'removeField', 'removeButton', 'placeholder']
  // aspect: the crop shape (1 = square artwork; 3 = a 3:1 profile banner).
  // minWidth/minHeight: the smallest image accepted
  static values = { url: String, aspect: { type: Number, default: 1 }, minWidth: { type: Number, default: MIN_SIZE }, minHeight: { type: Number, default: MIN_SIZE } }

  disconnect() {
    this.destroyCropper()
  }

  browse(event) {
    event.preventDefault()
    this.inputTarget.click()
  }

  keydown(event) {
    if (event.key === 'Enter' || event.key === ' ') this.browse(event)
  }

  picked() {
    const file = this.inputTarget.files[0]
    this.inputTarget.value = ''
    if (file) this.load(file)
  }

  dragover(event) {
    event.preventDefault()
    this.element.classList.add('image-upload--dragging')
  }

  dragleave() {
    this.element.classList.remove('image-upload--dragging')
  }

  drop(event) {
    event.preventDefault()
    this.dragleave()
    const file = event.dataTransfer.files[0]
    if (file) this.load(file)
  }

  paste(event) {
    // Don't hijack pastes into text fields
    if (event.target.closest('input, textarea, [contenteditable]')) return
    const item = Array.from(event.clipboardData?.items || []).find((i) => i.type.startsWith('image/'))
    if (!item) return
    event.preventDefault()
    this.load(item.getAsFile())
  }

  async load(file) {
    this.clearError()
    if (!ACCEPTED_TYPES.includes(file.type)) return this.showError('Please choose a JPEG or PNG image.')
    if (file.size > MAX_BYTES) return this.showError('Images must be smaller than 10MB.')

    const url = URL.createObjectURL(file)
    const { width, height } = await imageSize(url).catch(() => ({ width: 0, height: 0 }))
    if (width < this.minWidthValue || height < this.minHeightValue) {
      URL.revokeObjectURL(url)
      return this.showError(`Images must be at least ${this.minWidthValue}×${this.minHeightValue} pixels (this one is ${width}×${height}).`)
    }

    this.file = file
    this.openCropper(url)
  }

  openCropper(url) {
    this.destroyCropper()
    this.objectUrl = url
    this.cropImageTarget.src = url
    // Open before creating the cropper - it measures the visible container
    this.modalTarget.showModal()
    this.cropper = new Cropper(this.cropImageTarget, {
      aspectRatio: this.aspectValue,
      viewMode: 1,
      autoCropArea: 1,
      background: false,
    })
  }

  cancel(event) {
    event?.preventDefault()
    this.closeCropper()
  }

  skipCrop(event) {
    event.preventDefault()
    const file = this.file
    this.closeCropper()
    this.upload(file)
  }

  confirmCrop(event) {
    event.preventDefault()
    const canvas = this.cropper.getCroppedCanvas({
      maxWidth: MAX_OUTPUT,
      maxHeight: MAX_OUTPUT,
      imageSmoothingQuality: 'high',
    })
    const name = this.file.name.replace(/\.\w+$/, '') + '.jpg'
    this.closeCropper()
    canvas.toBlob((blob) => {
      this.upload(new File([blob], name, { type: 'image/jpeg' }))
    }, 'image/jpeg', 0.9)
  }

  upload(file) {
    this.showPreview(file)
    this.setProgress(0)
    this.progressTarget.hidden = false
    this.labelTarget.textContent = 'Uploading…'

    const upload = new DirectUpload(file, this.urlValue, this)
    upload.create((error, blob) => {
      this.progressTarget.hidden = true
      if (error) {
        this.labelTarget.textContent = 'Drop, paste or click to upload'
        return this.showError('Upload failed - please try again.')
      }
      this.fieldTarget.value = blob.signed_id
      this.fieldTarget.disabled = false
      this.removeFieldTarget.value = '0'
      this.removeButtonTarget.hidden = false
      this.labelTarget.textContent = 'Uploaded - save to apply'
    })
  }

  // Removes whatever artwork is showing (upload, legacy Cloudinary image or
  // Apple Music artwork) when the form is saved.
  // "Pull artwork and details from Apple Music" found artwork (Track/Release
  // reflex): use it, replacing the saved upload and any unsaved one
  appleArtwork(event) {
    const { url, alt } = event.detail || {}
    if (!url) return

    this.clearError()
    this.removeFieldTarget.value = '1'
    this.fieldTarget.value = ''
    this.fieldTarget.disabled = true

    const wrapper = document.createElement('div')
    wrapper.className = 'position-relative'
    const img = document.createElement('img')
    img.src = url
    img.alt = alt || ''
    wrapper.appendChild(img)
    this.previewTarget.replaceChildren(wrapper)

    this.removeButtonTarget.hidden = false
    this.labelTarget.textContent = 'Apple Music artwork - save to apply'
  }

  remove(event) {
    event.preventDefault()
    this.clearError()
    this.removeFieldTarget.value = '1'
    this.fieldTarget.value = ''
    this.fieldTarget.disabled = true

    const appleArtwork = this.element.closest('form')?.querySelector('input[name$="[image_url]"]')
    if (appleArtwork) appleArtwork.value = ''

    this.previewTarget.replaceChildren(this.placeholderTarget.content.cloneNode(true))
    this.removeButtonTarget.hidden = true
    this.labelTarget.textContent = 'Image removed - save to apply'
  }

  // DirectUpload delegate hook - lets us track progress on the XHR
  directUploadWillStoreFileWithXHR(request) {
    request.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable) this.setProgress((event.loaded / event.total) * 100)
    })
  }

  showPreview(file) {
    const img = document.createElement('img')
    img.src = URL.createObjectURL(file)
    img.alt = 'New image preview'
    this.previewTarget.replaceChildren(img)
  }

  setProgress(percent) {
    this.progressBarTarget.style.width = `${Math.round(percent)}%`
  }

  showError(message) {
    this.errorTarget.textContent = message
    this.errorTarget.hidden = false
  }

  clearError() {
    this.errorTarget.textContent = ''
    this.errorTarget.hidden = true
  }

  closeCropper() {
    if (this.modalTarget.open) this.modalTarget.close()
    this.destroyCropper()
  }

  destroyCropper() {
    this.cropper?.destroy()
    this.cropper = null
    if (this.objectUrl) URL.revokeObjectURL(this.objectUrl)
    this.objectUrl = null
  }
}

function imageSize(url) {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight })
    img.onerror = reject
    img.src = url
  })
}
